import SectionCard from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import { appstr } from "@coreapp/utils/appstr";
import { Button, Checkbox, Flex, Icon, Menu, Select, Text } from "@theorgchartdev/storybook";
import EditableItemGroup from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { getFeatureAccess, getHoverTipContent } from "@/Utils/Misc";
import { CustomMappingPopover } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/CustomMappingPopover";
import * as React from "react";
import { kebabCase } from "@/Utils/formatters";
import {
  GetOptionalAuxFields,
  MAPPING_TEST_PREFIX
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/Utils";
import { enumFeaturesList } from "@coreapp/constants";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradeCard from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeCard";
import { UpgradeInfoCard } from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeInfoCard";

export const AdditionalReportsMappingSection = ({
  localConfig,
  BuildFieldMappingOptions,
  disableDropdowns,
  handleFieldRename,
  handleCustomFieldNameValidation,
  onUpdate
}) => {
  const { showPricingAndPackaging } = usePermissionCheck();
  const auxFields = GetOptionalAuxFields(localConfig);
  const canAddAdditionalReport =
    getFeatureAccess(enumFeaturesList.auxMapping) && auxFields && auxFields.length < 4;

  const additionalReportAccess =
    showPricingAndPackaging && getFeatureAccess(enumFeaturesList.auxMapping);

  const handleUpdateAdditionalFields = option => {
    let nextFieldMap = structuredClone(localConfig["fields"]);
    var headerToLink = null;

    if (localConfig["headers"] && localConfig["headers"].length > 0) {
      for (var header in localConfig["headers"]) {
        if (localConfig["headers"][header].includes("Aux")) {
          headerToLink = localConfig["headers"][header];
        }
      }
      if (headerToLink === null) {
        var index = 0;
        headerToLink = localConfig["headers"][index];
      }
    }
    if (headerToLink == null) headerToLink = "None";

    nextFieldMap[option.value] = headerToLink;
    onUpdate({ fields: nextFieldMap }, "fields");
  };

  const handleRemoveAdditionalReportMap = key => {
    let nextFieldMap = structuredClone(localConfig["fields"]);
    delete nextFieldMap[key];

    let updateObject = { fields: nextFieldMap };
    let changeSections = ["fields"];

    if (localConfig["auxDirectReports"]) {
      let existingAuxList = [...localConfig["auxDirectReports"]];
      var anIdx = existingAuxList.indexOf(key);
      if (anIdx >= 0) existingAuxList.splice(anIdx, 1);
      updateObject["auxDirectReports"] = existingAuxList;
      changeSections.push("auxDirectReports");
    }

    onUpdate(updateObject, changeSections);
  };

  const handleToggleARTDirReports = key => {
    let existingAuxList = [key];
    if (localConfig["auxDirectReports"]) {
      existingAuxList = [...localConfig["auxDirectReports"]];
      var anIdx = existingAuxList.indexOf(key);
      if (anIdx >= 0) existingAuxList.splice(anIdx, 1);
      else existingAuxList.push(key);
    }

    onUpdate({ auxDirectReports: existingAuxList }, "auxDirectReports");
  };

  const buildAdditionalFields = () => {
    var label = appstr("Additional reports to");
    var auxCode = "AuxSuperID";
    var fields = localConfig["fields"];
    var highestCount = -1;
    let options = [];
    let option = {};

    for (var aField in fields) {
      if (aField.includes("AuxSuperID")) {
        var split = aField.split("-");
        if (split.length === 1) {
          highestCount = 0;
        } else if (parseInt(split[1]) > highestCount) {
          highestCount = parseInt(split[1]);
        }
      }
    }

    if (highestCount >= 0) {
      highestCount++;
      label += ` ${highestCount}`;
      auxCode += "-" + highestCount;
      option.label = label;
      option.value = auxCode;
      option.clickHandler = handleUpdateAdditionalFields;
    }

    if (highestCount == -1) {
      option.label = label;
      option.value = auxCode;
      option.clickHandler = handleUpdateAdditionalFields;
    }

    options.push(option);
    return options;
  };

  const showHelp = !auxFields || auxFields.length === 0;

  let customCardStyle = {};
  let titleContent = null;

  if (!showPricingAndPackaging || getFeatureAccess(enumFeaturesList.auxMapping)) {
    titleContent = (
      <Menu
        options={buildAdditionalFields()}
        trigger={
          <Button
            modifier="secondary"
            iconProps={{ name: "plus" }}
            disabled={!canAddAdditionalReport}
            dataTestId="add-additional-report-field-btn"
            tooltipProps={
              !canAddAdditionalReport
                ? {
                    message: appstr("Additional reports limit")
                  }
                : undefined
            }
          >
            {appstr("Add additional report field")}
          </Button>
        }
        listHeader={{
          label: appstr("Select field")
        }}
        listProps={{ maxHeight: "440px" }}
        position="bottom-start"
        listWidth={"large"}
        stayAlive={true}
        fillWidth={false}
      />
    );
  }

  if (showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.auxMapping)) {
    customCardStyle = { background: "var(--bg-brand-secondary)" };
  }

  const arUpgradeMessageDynamicText = {
    template: appstr("ar upgrade template"),
    formatConfig: { bold1: { type: "bold" } },
    replaceDict: { bold1: appstr("accurate depiction") }
  };

  return (
    <SectionCard
      title={appstr("AuxRepMapping")}
      customCardStyle={customCardStyle}
      titleContent={titleContent}
    >
      <Flex direction={"column"} gap={"16px"}>
        {showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.auxMapping) && (
          <UpgradeCard iconName={"users-medical-regular"} featureKey={enumFeaturesList.auxMapping}>
            <UpgradeInfoCard.Body dynamic={arUpgradeMessageDynamicText}>
              {appstr("ar upgrade message")}
            </UpgradeInfoCard.Body>
          </UpgradeCard>
        )}

        {showHelp && getFeatureAccess(enumFeaturesList.auxMapping) && (
          <Text>{appstr("Aux details")}</Text>
        )}
        {auxFields &&
          getFeatureAccess(enumFeaturesList.auxMapping) &&
          auxFields.map((key, index) => (
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              key={`${localConfig.fields?.[key]}-${index}`}
            >
              <Flex direction={"column"} gap={"8px"} width={"50%"}>
                <EditableItemGroup
                  actions={[
                    {
                      iconName: "trash-can",
                      iconProps: { color: "error" },
                      onClick: () => handleRemoveAdditionalReportMap(key),
                      dataTestId: `aux-rep-mapping-${key}-remove`
                    }
                  ]}
                  dataTestId={`aux-rep-mapping-${key}`}
                >
                  <Flex gap={"4px"}>
                    <Select
                      dataTestId={`${MAPPING_TEST_PREFIX}-aux-select-${kebabCase(key)}`}
                      label={`${appstr("Additional reports to")} ${index > 0 ? index : ""}`}
                      inputOnly={false}
                      placeholder={localConfig.fields?.[key] ?? appstr("Select field")}
                      value={localConfig.fields?.[key]}
                      options={BuildFieldMappingOptions(key)}
                      selectSize={"medium"}
                      optionSize={"medium"}
                      listProps={FieldSelectListProps()}
                      disabled={disableDropdowns}
                    />
                    <Flex
                      className={"oc-right-justify"}
                      gap={"32px"}
                      padding={"30px"}
                      alignItems={"center"}
                    >
                      <Checkbox
                        checked={localConfig["auxDirectReports"]?.includes(key)}
                        id={"aux-direct-cb-" + key}
                        data-testid={`${MAPPING_TEST_PREFIX}-aux-direct-reports-checkbox-${kebabCase(key)}`}
                        label={appstr("Show Direct Reports")}
                        onToggle={() => handleToggleARTDirReports(key)}
                        tooltipProps={getHoverTipContent("bringAuxDirectReports")}
                      />
                    </Flex>
                  </Flex>
                </EditableItemGroup>
              </Flex>
              <CustomMappingPopover
                onUpdate={handleFieldRename}
                fieldID={key}
                currentField={localConfig["fields"] ? localConfig["fields"][key] : null}
                validateFxn={handleCustomFieldNameValidation}
              />
            </Flex>
          ))}
      </Flex>
    </SectionCard>
  );
};
